import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { adminAuth, setAdminAuthToken, ADMIN_TOKEN_KEY } from "@/lib/adminApi";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
    const [token, setTokenState] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY));
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(Boolean(token));

    const persistToken = useCallback((nextToken) => {
        if (nextToken) {
            localStorage.setItem(ADMIN_TOKEN_KEY, nextToken);
        } else {
            localStorage.removeItem(ADMIN_TOKEN_KEY);
        }
        setAdminAuthToken(nextToken);
        setTokenState(nextToken);
    }, []);

    useEffect(() => {
        setAdminAuthToken(token);
        if (!token) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        adminAuth
            .me()
            .then((a) => {
                if (!cancelled) setAdmin(a);
            })
            .catch(() => {
                if (!cancelled) {
                    persistToken(null);
                    setAdmin(null);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = useCallback(
        async ({ username, password }) => {
            const data = await adminAuth.login({ username, password });
            persistToken(data.access_token);
            setAdmin(data.admin);
            return data.admin;
        },
        [persistToken],
    );

    const logout = useCallback(() => {
        persistToken(null);
        setAdmin(null);
    }, [persistToken]);

    return (
        <AdminAuthContext.Provider
            value={{
                admin,
                token,
                loading,
                login,
                logout,
                isAdminAuthenticated: !!admin,
            }}
        >
            {children}
        </AdminAuthContext.Provider>
    );
}

export function useAdminAuth() {
    const ctx = useContext(AdminAuthContext);
    if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
    return ctx;
}
